import React from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { useClinic } from "../context/ClinicContext";

const Footer = () => {
  const { clinicInfo } = useClinic();
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <footer className="bg-[#1C1917] text-[#F5F5F4] pt-20 pb-10">
      <div className="container mx-auto px-8 lg:px-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* About */}
          <div className="space-y-5">
            <h3
              className="text-lg font-medium text-[#FAFAF9] mb-5 uppercase tracking-wider"
              style={{ fontFamily: "IBM Plex Sans, sans-serif" }}
            >
              {clinicInfo.name}
            </h3>
            <p className="body-regular text-[#D6D3D1] leading-relaxed">
              Family dental clinic focused on your overall oral health and
              well-being. American Board Certified dentist.
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-5">
            <h4 className="text-sm font-medium text-[#FAFAF9] mb-5 uppercase tracking-wider">
              Quick Links
            </h4>
            <ul className="space-y-4">
              <li>
                <button
                  onClick={() => scrollToSection("home")}
                  className="body-regular text-[#D6D3D1] hover:text-[#0F766E] transition-colors duration-300"
                >
                  Home
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("services")}
                  className="body-regular text-[#D6D3D1] hover:text-[#0F766E] transition-colors duration-300"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("about")}
                  className="body-regular text-[#D6D3D1] hover:text-[#0F766E] transition-colors duration-300"
                >
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("testimonials")}
                  className="body-regular text-[#D6D3D1] hover:text-[#0F766E] transition-colors duration-300"
                >
                  Testimonials
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection("faq")}
                  className="body-regular text-[#D6D3D1] hover:text-[#0F766E] transition-colors duration-300"
                >
                  FAQ
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="space-y-5">
            <h4 className="text-sm font-medium text-[#FAFAF9] mb-5 uppercase tracking-wider">
              Contact Us
            </h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${clinicInfo.phone}`}
                  className="flex items-center gap-3 text-[#D6D3D1] hover:text-[#0F766E] transition-colors duration-300 group"
                >
                  <Phone
                    size={18}
                    strokeWidth={1.5}
                    className="flex-shrink-0"
                  />
                  <span className="body-regular">{clinicInfo.phone}</span>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${clinicInfo.email}`}
                  className="flex items-center gap-3 text-[#D6D3D1] hover:text-[#0F766E] transition-colors duration-300 group"
                >
                  <Mail size={18} strokeWidth={1.5} className="flex-shrink-0" />
                  <span className="body-regular">{clinicInfo.email}</span>
                </a>
              </li>
              <li>
                <a
                  href={`https://wa.me/${clinicInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-[#D6D3D1] hover:text-[#0F766E] transition-colors duration-300 group"
                >
                  <MessageCircle
                    size={18}
                    strokeWidth={1.5}
                    className="flex-shrink-0"
                  />
                  <span className="body-regular">WhatsApp</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Office Hours & Location */}
          <div className="space-y-5">
            <h4 className="text-sm font-medium text-[#FAFAF9] mb-5 uppercase tracking-wider">
              Visit Us
            </h4>
            <div className="space-y-5">
              <div className="flex items-start gap-3">
                <Clock
                  size={18}
                  strokeWidth={1.5}
                  className="flex-shrink-0 mt-1 text-[#D6D3D1]"
                />
                <div>
                  <p className="body-regular text-[#D6D3D1]">
                    {clinicInfo.hours.weekdays}
                  </p>
                  <p className="body-regular text-[#D6D3D1]">
                    {clinicInfo.hours.time}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin
                  size={18}
                  strokeWidth={1.5}
                  className="flex-shrink-0 mt-1 text-[#D6D3D1]"
                />
                <div>
                  <p className="body-regular text-[#D6D3D1]">
                    {clinicInfo.address.street}
                  </p>
                  <p className="body-regular text-[#D6D3D1]">
                    {clinicInfo.address.area}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="py-16 mb-12 border-t border-b border-[#57534E]">
          <div className="text-center space-y-8">
            <p
              className="text-3xl sm:text-4xl font-medium text-white"
              style={{
                fontFamily: "Cormorant, Georgia, serif",
                letterSpacing: "-0.02em",
              }}
            >
              Ready to improve your smile?
            </p>
            <button
              onClick={() => scrollToSection("appointments")}
              className="inline-flex items-center justify-center gap-2 px-10 py-5 text-base font-semibold text-white bg-[#0F766E] border-2 border-[#0F766E] rounded-md shadow-lg hover:bg-[#0D6460] hover:border-[#0D6460] hover:shadow-xl active:scale-95 transition-all duration-300 uppercase tracking-wide"
              style={{
                fontFamily: "IBM Plex Sans, sans-serif",
              }}
            >
              Book Appointment Now
            </button>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center pt-10 border-t border-[#57534E]">
          <p className="body-small text-[#A8A29E]">
            © {new Date().getFullYear()} {clinicInfo.name}. All rights reserved.
          </p>
        </div>
      </div>

      {/* Floating WhatsApp Button - Mobile */}
      <a
        href={`https://wa.me/${clinicInfo.whatsapp}`}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-16 h-16 bg-[#25D366] text-white flex items-center justify-center rounded-full shadow-elevated hover:scale-110 transition-transform duration-300 lg:hidden z-50"
        aria-label="Contact us on WhatsApp"
      >
        <MessageCircle size={28} strokeWidth={1.5} />
      </a>

      {/* Floating Call Button - Mobile */}
      <a
        href={`tel:${clinicInfo.phone}`}
        className="fixed bottom-6 left-6 w-16 h-16 bg-[#0F766E] text-white flex items-center justify-center rounded-full shadow-elevated hover:scale-110 transition-transform duration-300 lg:hidden z-50"
        aria-label="Call us now"
      >
        <Phone size={26} strokeWidth={1.5} />
      </a>
    </footer>
  );
};

export default Footer;
